function isIgnorableElement(element: Element) {
  return (
    element.getAttribute('aria-hidden') === 'true' ||
    element.tagName.toLowerCase() === 'script' ||
    element.tagName.toLowerCase() === 'style'
  );
}

function getTextNodes(root: HTMLElement) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ALL, {
    acceptNode(node) {
      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;

        if (isIgnorableElement(element)) {
          return NodeFilter.FILTER_REJECT;
        }

        if (element.tagName.toLowerCase() === 'br') {
          return NodeFilter.FILTER_ACCEPT;
        }
      }

      if (node.nodeType === Node.TEXT_NODE && (node.textContent?.length ?? 0) > 0) {
        return NodeFilter.FILTER_ACCEPT;
      }

      return NodeFilter.FILTER_SKIP;
    },
  });

  const nodes: Node[] = [];
  let currentNode = walker.nextNode();

  while (currentNode) {
    nodes.push(currentNode);
    currentNode = walker.nextNode();
  }

  return nodes;
}

interface OffsetPoint {
  node: Node;
  offset: number;
}

function resolveBoundaryPoint(
  root: HTMLElement,
  targetOffset: number,
  fallbackToEnd: boolean,
): OffsetPoint | null {
  const nodes = getTextNodes(root);
  let cursor = 0;

  for (const node of nodes) {
    if (node.nodeType === Node.ELEMENT_NODE) {
      if (cursor === targetOffset) {
        const parent = node.parentNode;

        if (!parent) {
          return null;
        }

        const index = Array.from(parent.childNodes).indexOf(node as ChildNode);
        return {
          node: parent,
          offset: fallbackToEnd ? index + 1 : index,
        };
      }

      cursor += 1;
      continue;
    }

    const length = node.textContent?.length ?? 0;

    if (targetOffset <= cursor + length) {
      return {
        node,
        offset: targetOffset - cursor,
      };
    }

    cursor += length;
  }

  if (!fallbackToEnd) {
    return null;
  }

  return {
    node: root,
    offset: root.childNodes.length,
  };
}

export function getTextOffsetRange(
  root: HTMLElement,
  startOffset: number,
  endOffset: number,
) {
  if (endOffset <= startOffset) {
    return null;
  }

  const start = resolveBoundaryPoint(root, startOffset, false);
  const end = resolveBoundaryPoint(root, endOffset, true);

  if (!start || !end) {
    return null;
  }

  const range = document.createRange();
  range.setStart(start.node, start.offset);
  range.setEnd(end.node, end.offset);
  return range;
}
